import React, { useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import { useNavigate } from 'react-router-dom';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  font-family: ${theme.typography.fontFamily};
  padding: ${theme.spacing.xl};
`;

const Modal = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xxxl};
  max-width: 480px;
  width: 100%;
  box-shadow: ${theme.shadows.xxl};
  border: 1px solid ${theme.colors.border};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: transparent;
  border: none;
  font-size: ${theme.typography.fontSizes.xxl};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  line-height: 1;
  
  &:hover {
    color: ${theme.colors.text};
  }
`;

const Title = styled.h2`
  font-size: ${theme.typography.fontSizes.xxl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const Message = styled.p`
  font-size: ${theme.typography.fontSizes.base};
  color: ${theme.colors.text};
  text-align: center;
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.xl} 0;
`;

const Feature = styled.li`
  padding: ${theme.spacing.sm} 0;
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.base};
  
  &:before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
    font-size: ${theme.typography.fontSizes.base};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const StyledButton = styled(Button)`
  flex: 1;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
`;

const UpgradeModal = ({ isOpen, onClose, reason = 'limit' }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUpgradeClick = () => {
    navigate('/plans');
  };

  const handleViewBillingClick = () => {
    navigate('/billing');
  };

  const getModalContent = () => {
    switch (reason) {
      case 'limit':
        return {
          title: 'Usage Limit Reached',
          message: 'You\'ve reached your monthly limit for object labeling. Upgrade to continue or wait for your limit to reset next month.'
        };
      case 'trial_expired':
        return {
          title: 'Free Trial Expired',
          message: 'Your 7-day free trial has ended. Subscribe to a plan to continue using all features.'
        };
      case 'subscription_expired':
        return {
          title: 'Subscription Expired',
          message: 'Your subscription has expired. Please renew to continue accessing the service.'
        };
      default:
        return {
          title: 'Upgrade Your Plan',
          message: 'Get more out of your account with a subscription.'
        };
    }
  };

  const content = getModalContent();

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <Title>{content.title}</Title>
        <Message>{content.message}</Message>

        <FeatureList>
          <Feature>Choose from 2 flexible plans</Feature>
          <Feature>Unlimited labels with Pro plan</Feature>
          <Feature>Priority support</Feature>
          <Feature>Cancel anytime</Feature>
        </FeatureList>

        <ButtonGroup>
          <StyledButton
            onClick={handleUpgradeClick}
            variant="primary"
          >
            View Plans
          </StyledButton>
          <StyledButton
            onClick={handleViewBillingClick}
            variant="ghost"
          >
            View Billing
          </StyledButton>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default UpgradeModal;
